import { useCallback, useEffect, useState } from 'react';
import {
  useDismissLatestRecapMutation,
  useGetLatestRecapLazyQuery,
  useGetPlayerReflectionByLessonIdLazyQuery,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import { LatestRecapType } from 'screens/PlayerLoggedInScreen/props';

/**
 * Fetches the player's most recent lesson recap and only surfaces it when the
 * player hasn't yet completed their own reflection for that lesson. Ported from
 * the v1 PlayerLoggedInScreen.
 *
 * Dismissals are persisted to the DB via `lesson_reflections.player_dismissed_at`.
 * We look at the single most recent reflection and only surface it while it is
 * undismissed, so once the player dismisses it nothing shows until a *newer*
 * reflection is created (which comes through with player_dismissed_at = null).
 */
export function useLatestRecap() {
  const viewer = useViewer();
  const [recap, setRecap] = useState<LatestRecapType | null>(null);

  const [getLatestRecap] = useGetLatestRecapLazyQuery();
  const [getPlayerReflectionByLessonId] = useGetPlayerReflectionByLessonIdLazyQuery();
  const [dismissLatestRecap] = useDismissLatestRecapMutation();

  useEffect(() => {
    const init = async () => {
      if (!viewer.userId) return;

      const recapResponse = await getLatestRecap({
        variables: { playerUserId: viewer.userId },
      });

      const latest = recapResponse.data?.lessonReflections[0];
      if (!latest || latest.playerDismissedAt) return;

      const reflectionResponse = await getPlayerReflectionByLessonId({
        variables: { lessonId: latest.lessonId, playerUserId: viewer.userId },
      });

      const playerReflection = reflectionResponse.data?.lessonPlayerSkillAssessments?.[0];
      if (!playerReflection) {
        setRecap(latest);
      }
    };

    init();
  }, [viewer.userId]);

  const dismissRecap = useCallback(() => {
    if (!recap) return;
    dismissLatestRecap({
      variables: { id: recap.id, playerDismissedAt: new Date().toISOString() },
    });
  }, [recap, dismissLatestRecap]);

  return { recap, dismissRecap };
}
